/**
 * Custom Icons Tab - Upload and manage custom icons
 */

import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faUpload, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { ICON_LIMITS } from '../../../../../services/iconService';
import DeleteIconButton from './DeleteIconButton';

const CustomIconsTab = ({
  customIcons,
  isLoading,
  error,
  uploadProgress,
  uploadError,
  isUploading,
  deletingIconId,
  confirmDeleteId,
  currentIcon,
  onSelect,
  onUpload,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  onClearError,
  onRetryLoad,
}) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // Reset input
    if (file) onUpload(file);
  };

  return (
    <div className="icon-picker__custom-tab">
      {/* Upload zone */}
      <label className={`icon-picker__upload-zone ${isUploading ? 'icon-picker__upload-zone--uploading' : ''}`}>
        {isUploading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <span className="icon-picker__upload-text">Uploading... {uploadProgress}%</span>
            <div className="icon-picker__upload-progress">
              <div 
                className="icon-picker__upload-progress-bar" 
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faUpload} size="2x" />
            <span className="icon-picker__upload-text">Upload custom icon</span>
            <span className="icon-picker__upload-hint">
              PNG, SVG, WebP, JPEG • Auto-compressed
            </span>
          </>
        )}
        <input 
          ref={fileInputRef}
          type="file" 
          className="icon-picker__upload-input" 
          accept=".png,.svg,.webp,.jpg,.jpeg,image/png,image/svg+xml,image/webp,image/jpeg"
          onChange={handleFileSelect}
          disabled={isUploading}
        />
      </label>

      {/* Error message */}
      {uploadError && (
        <div className="icon-picker__upload-error">
          <FontAwesomeIcon icon={faExclamationCircle} />
          {uploadError}
          <button onClick={onClearError}>×</button>
        </div>
      )}

      {/* Custom icons grid */}
      {isLoading ? (
        <div className="icon-picker__custom-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          Loading your icons...
        </div>
      ) : error ? (
        <div className="icon-picker__custom-error">
          Failed to load icons. <button onClick={onRetryLoad}>Retry</button>
        </div>
      ) : customIcons.length === 0 ? (
        <div className="icon-picker__custom-empty">
          <span>No custom icons yet</span>
          <span className="icon-picker__custom-empty-hint">
            Upload your own logos, team avatars, or project icons
          </span>
        </div>
      ) : (
        <div className="ip-section">
          <div className="ip-section__header">
            Your Icons ({customIcons.length}/{ICON_LIMITS.maxIconsPerUser})
          </div>
          <div className="ip-section__grid">
            {customIcons.map((icon) => (
              <div key={icon.iconId} className="ip-btn-wrapper">
                <button
                  className={`ip-btn ip-btn--custom ${currentIcon?.type === 'custom' && currentIcon?.iconId === icon.iconId ? 'ip-btn--selected' : ''}`}
                  onClick={() => onSelect({ 
                    type: 'custom', 
                    iconId: icon.iconId,
                    publicUrl: icon.publicUrl,
                    fileName: icon.fileName,
                  })}
                  title={icon.fileName}
                >
                  <img 
                    src={icon.publicUrl} 
                    alt={icon.fileName}
                    className="ip-btn__custom-img"
                  />
                </button>
                {confirmDeleteId === icon.iconId ? (
                  <div className="ip-btn__confirm-delete">
                    <span>Delete?</span>
                    <button 
                      className="ip-btn__confirm-yes"
                      onClick={() => onConfirmDelete(icon.iconId)}
                    >
                      Yes
                    </button>
                    <button 
                      className="ip-btn__confirm-no"
                      onClick={onCancelDelete}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <DeleteIconButton
                    onClick={(e) => onRequestDelete(icon.iconId, e)}
                    isDeleting={deletingIconId === icon.iconId}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomIconsTab;

